/** @typedef {{ id: number | string, value: string }} SampleRow */

/**
 * Normalizes `decoder` row payloads into `{ id, value }[]` for the Data tab.
 * Uses `{ id | sampleId }` from array elements when present, else `data[].id` from legacy slots,
 * else zero-based index.
 * @param {object} row
 * @returns {SampleRow[]}
 */
export function getSampleRowsWithIds(row) {
  const raw = row?.raw_data;

  /** @type {unknown[]} */
  let list = [];

  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw == null) {
    const slots = Array.isArray(row?.data) ? row.data : [];
    if (slots.length > 0) {
      return slots.map((slot, i) => ({
        id: slot.id ?? i,
        value: formatSampleValue(slot.data),
      }));
    }
  } else if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("[")) {
      try {
        const parsed = JSON.parse(t);
        list = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        list = [raw];
      }
    } else {
      list = [raw];
    }
  } else if (typeof raw === "number" || typeof raw === "boolean") {
    list = [raw];
  } else if (typeof raw === "object") {
    try {
      list = [JSON.stringify(raw)];
    } catch {
      list = [String(raw)];
    }
  }

  return list.map((item, index) => resolveSampleRow(item, index));
}

/**
 * @param {unknown} item
 * @param {number} index
 * @returns {SampleRow}
 */
function resolveSampleRow(item, index) {
  if (item != null && typeof item === "object" && !Array.isArray(item)) {
    const o = /** @type {Record<string, unknown>} */ (item);
    if ("id" in o || "sampleId" in o) {
      const rawSid = o.id ?? o.sampleId;
      let displayId = index;
      if (typeof rawSid === "number" && Number.isFinite(rawSid)) {
        displayId = rawSid;
      } else if (typeof rawSid === "string" && rawSid.trim() !== "") {
        const n = Number(rawSid);
        displayId = Number.isFinite(n) ? n : rawSid.trim();
      }
      const payload = "value" in o ? o.value : "data" in o ? o.data : item;
      return { id: displayId, value: formatSampleValue(payload) };
    }
  }
  return { id: index, value: formatSampleValue(item) };
}

/**
 * @param {unknown} value
 */
function formatSampleValue(value) {
  if (value === null || value === undefined) return String(value);
  if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
